/* Visibility Mesh homepage behaviour. Three jobs, each small.
   1. Analytics: any element with data-vm-event pushes one event to window.dataLayer (and gtag when present). This is the only click
      listener for data-vm-event on the site (the inline copies on Managed, Pricing, Enterprise and the case study were removed on 3 October 2026).
   2. The proof strip figures ([data-vm-bench]) fill from the live benchmark (hour cached, CORS allows visibilitymesh.com).
   3. Every scan bar (hero and final call to action) posts to the same engine endpoint and fields as snippets/vm-free-scan.liquid,
      then runs the scan sequence (2b) and publishes scan_start and scan_complete once each.
   4. Proposal modal: any element with data-vm-proposal opens snippets/vm-proposal-modal.liquid on every page.
   5. Reveal on scroll for elements with class rv inside .vm-home. */
(function () {
  "use strict";
  if (window.__vmHomeJsLoaded) return;
  window.__vmHomeJsLoaded = true;
  var ENGINE = "https://answerops-engine-production.up.railway.app";

  // 1. analytics
  document.addEventListener("click", function (e) {
    var el = e.target.closest ? e.target.closest("[data-vm-event]") : null;
    if (!el) return;
    var payload = {
      event: el.getAttribute("data-vm-event"),
      vm_location: el.getAttribute("data-vm-location") || "",
      vm_plan: el.getAttribute("data-vm-plan") || "",
      vm_label: el.getAttribute("data-vm-label") || (el.textContent || "").replace(/\s+/g, " ").trim().slice(0, 60)
    };
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
      if (typeof window.gtag === "function") window.gtag("event", payload.event, payload);
    } catch (err) { /* never block navigation */ }
    // The first party collector hears clicks under one name, vm_cta_click, with the click name in the data
    // (pricing_path_select, managed_cta_click and the rest). No GA4 pixel subscribes to it, so GA4 still counts each click once, through gtag above.
    vmPublish("vm_cta_click", { event: payload.event, location: payload.vm_location, plan: payload.vm_plan, page: window.location.pathname });
  }, true);

  // 1b. analytics for elements tagged data-vm-track (app page and app showcase)
  document.addEventListener("click", function (e) {
    var el = e.target.closest ? e.target.closest("[data-vm-track]") : null;
    if (!el) return;
    var payload = {
      event: el.getAttribute("data-vm-track"),
      vm_location: el.getAttribute("data-vm-location") || "",
      vm_label: (el.textContent || "").replace(/\s+/g, " ").trim().slice(0, 60)
    };
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
      if (typeof window.gtag === "function") window.gtag("event", payload.event, payload);
    } catch (err) { /* never block navigation */ }
  }, true);

  // 2. live benchmark figures (homepage proof strip and any other [data-vm-bench] slot).
  //    n = current benchmark population; scored_to_date = every site scored to date, shown as a floor to the hundred plus "+",
  //    the same rule the daily numbers sync uses for the crawlable figure in templates/index.json. On failure the slot shows the synced figure from the
  //    benchmark block, then the server rendered fallback; only a slot with neither hides its whole item.
  var spots = document.querySelectorAll("[data-vm-bench]");
  if (spots.length && window.fetch) {
    // When the live request fails, use the synced figure from the benchmark block on the same page (the anchored text the daily sync rewrites).
    var anchoredFig = function (key) {
      var lead = { scored_to_date: "websites scored", n: "of them sit", median: "median score" }[key];
      if (!lead) return "";
      var nodes = document.querySelectorAll("#benchmark .n.accent");
      for (var k = 0; k < nodes.length; k++) {
        var lab = nodes[k].nextElementSibling;
        if (lab && lab.textContent.trim().indexOf(lead) === 0) return nodes[k].textContent.trim();
      }
      return "";
    };
    var benchFill = function (b) {
      for (var i = 0; i < spots.length; i++) {
        var el = spots[i], key = el.getAttribute("data-vm-bench"), v = null;
        if (b && key === "n" && typeof b.n === "number") v = String(b.n);
        if (b && key === "scored_to_date" && typeof b.scored_to_date === "number") v = (Math.floor(b.scored_to_date / 100) * 100) + "+";
        if (b && key === "median" && typeof b.median === "number") v = String(b.median);
        if (!v) v = anchoredFig(key) || el.getAttribute("data-vm-fallback") || el.textContent.trim();
        if (v) { el.textContent = v; el.removeAttribute("aria-hidden"); }
        else { var li = el.closest ? el.closest("li") : null; if (li) li.hidden = true; }
      }
    };
    fetch(ENGINE + "/benchmark?cohort=all").then(function (r) { return r.ok ? r.json() : null; }).then(benchFill).catch(function () { benchFill(null); });
  }

  // helpers shared by the scan bars and the scan sequence
  var vmPush = function (payload) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
      if (typeof window.gtag === "function") window.gtag("event", payload.event, payload);
    } catch (err) { /* never block */ }
  };
  var vmPublish = function (name, data) {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === "function") Shopify.analytics.publish(name, data);
    } catch (err) { /* never block */ }
  };
  var vmEsc = function (s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) { return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]; });
  };


  // 2b. the scan sequence (directive section 11), written to the E_scan_sequence.md specification.
  //     The engine reports one coarse status (queued, crawling, crawled, scoring, reporting, done, needs_review, error), so:
  //     01 FIND follows the crawl, and 02 to 05 share the scoring phase and resolve together when the scan is done.
  //     The gate lines show progress states only (Waiting, Checking, Done) and never a score: the engine has no approved
  //     category to gate map, so category scores are never relabelled as gates. Results use the real category names.
  //     When the scan is done, GET /scans/{id}/summary feeds two places: the short summary with the two actions on the left,
  //     and the visitor's own report, which replaces our sample report on the right (renderReport). Every API string is escaped.
  var GATES = [
    { key: "find", name: "Find" },
    { key: "read", name: "Read" },
    { key: "follow", name: "Follow" },
    { key: "quote", name: "Quote" },
    { key: "trust", name: "Trust" }
  ];
  // the order of the report categories on our sample report; any category the engine adds later follows in its own order
  var CAT_ORDER = ["Technical Foundations", "Schema Structured Data", "Semantic Architecture", "AI Readability AEO", "Authority Operational Discipline"];
  var bandOf = function (pct) { return pct < 40 ? "Starting Point" : pct < 60 ? "Developing" : pct < 80 ? "Established" : "Leading"; };
  var stateOf = function (pct) { return pct >= 80 ? "lead" : pct >= 60 ? "pass" : pct >= 40 ? "partial" : "fail"; };
  var num = function (v) { var n = Number(v); if (!isFinite(n)) return ""; return n % 1 === 0 ? String(n) : (Math.round(n * 10) / 10).toFixed(1); };
  // engine sentences sometimes carry a spaced dash; the site uses a comma there
  var tidy = function (s) { return String(s == null ? "" : s).replace(/\s+[–—]\s+/g, ", ").replace(/\s+/g, " ").trim(); };
  var todayText = function () {
    try { return new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }); } catch (e) { return ""; }
  };
  var pathOf = function (u) { try { var p = new URL(u); return (p.pathname || "/") + (p.search || ""); } catch (e) { return String(u || ""); } };
  var categoriesOf = function (s) {
    var subs = (s && s.subtotals) || {};
    var names = Object.keys(subs).filter(function (k) { return subs[k] && subs[k].max; });
    names.sort(function (a, b) {
      var ia = CAT_ORDER.indexOf(a), ib = CAT_ORDER.indexOf(b);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });
    return names.map(function (k) { return { name: k, score: Number(subs[k].score) || 0, max: Number(subs[k].max) }; });
  };
  // Some engine findings are positive observations ("Entity model is coherent"); they are not priorities.
  // Engine backlog: classify these at the source. Until then the hero skips the obvious ones.
  var POSITIVE = /^[^.]*\b(is|are) (coherent|consistent|well structured)\b/i;
  // The engine falls back to one generic hint for many findings; the hero hides it until the engine sends specific ones.
  var GENERIC_HINT = /compare this page against a strong competitor/i;
  // one benchmark line, built only from the benchmark fields exactly as returned; empty when any of them is missing
  var benchLine = function (b) {
    // The engine compares every site with one cohort (today Shopify stores), so a percentile would be
    // misleading for a WordPress or WooCommerce site. State the cohort median as a reference instead.
    // Since engine PR 353 the summary names the cohort that matches the scanned site (cohort_basis set);
    // only then is a percentile shown. Older responses get the median as a plain reference.
    if (!b || typeof b.n !== "number" || !b.cohort) return "";
    if (b.cohort_basis && typeof b.percentile === "number") return "Higher than " + b.percentile + "% of the " + b.n + " " + b.cohort + " in the live benchmark.";
    if (typeof b.median !== "number") return "";
    return "For reference, the median score of the " + b.n + " " + b.cohort + " in the live benchmark is " + b.median + ".";
  };
  // the two equal options after a scan (Margareta, 4 October 2026): run a full scan, or ask us for a proposal.
  // Both are buttons of the same weight, side by side, full scan first; "See all results" is a small text link below them.
  var actionsHtml = function (ctx) {
    return "<div class='vmh-seq__acts'><div class='vmh-seq__pair'>" +
      "<a class='vm-btn vm-btn--primary vmh-seq__opt' href='/pages/pricing#measure' data-vm-event='scan_click' data-vm-location='scan_result'>Run a full scan</a>" +
      "<a class='vm-btn vm-btn--primary vmh-seq__opt' href='/pages/managed-ai-visibility#quote' data-vm-proposal" +
        " data-vm-pm-site='" + vmEsc(ctx.url) + "' data-vm-pm-email='" + vmEsc(ctx.email) + "' data-vm-pm-scan='" + vmEsc(ctx.id) + "'" +
        (ctx.score !== "" ? " data-vm-pm-score='" + vmEsc(ctx.score) + "'" : "") +
        " data-vm-event='managed_cta_click' data-vm-location='scan_result'>Fix it for me: get a proposal</a>" +
      "</div>" +
      "<p class='vmh-seq__promise'>A full scan reads more of your site and ranks every issue. Our consultants review your results and send a proposal with a roadmap and a quoted price within 1 business day.</p>" +
      "<a class='vmh-seq__all' href='" + ctx.resultsLink + "' data-vm-event='scan_results_click' data-vm-location='scan_result'>See all results</a>" +
      "</div>";
  };
  // the visitor's own report, in the same visual style as our sample report
  var renderReport = function (s, host) {
    var cats = categoriesOf(s);
    var max = Number(s.max_score) || 100;
    var pct = (Number(s.score) / max) * 100;
    var band = s.band || bandOf(pct);
    var bench = benchLine(s.benchmark);
    var issues = (Array.isArray(s.issues) ? s.issues.slice() : []).filter(function (x) { return x && x.problem && !POSITIVE.test(String(x.problem)); });
    // Pick the 3 priorities to show (4 October 2026): most severe first, then rank, and at most one per
    // category until every category has had a turn, so the sample shows the breadth of the diagnosis.
    var SEV = { critical: 0, high: 1, medium: 2, low: 3 };
    issues.sort(function (a, b) {
      var sa = SEV[String(a.severity || "").toLowerCase()], sb = SEV[String(b.severity || "").toLowerCase()];
      sa = sa == null ? 4 : sa; sb = sb == null ? 4 : sb;
      return sa - sb || (Number(a.rank) || 999) - (Number(b.rank) || 999);
    });
    var picked = [], seen = {};
    issues.forEach(function (x) { var c = x.category_name || ""; if (picked.length < 3 && !seen[c]) { seen[c] = 1; picked.push(x); } });
    issues.forEach(function (x) { if (picked.length < 3 && picked.indexOf(x) < 0) picked.push(x); });
    issues = picked;
    var pages = Array.isArray(s.pages_read) ? s.pages_read.filter(Boolean) : [];
    var date = todayText();
    var h = "<div class='vmh-rep vmh-rep--yours'>" +
      "<div class='vmh-rep__bar'><span class='vmh-rep__brand'>Visibility Report</span><span class='vmh-rep__tag'>Your report</span></div>" +
      "<ul class='vm-stamp vmh-rep__stamp'><li>" + vmEsc(host) + "</li>" + (date ? "<li>" + vmEsc(date) + "</li>" : "") + "<li>Free sample</li></ul>" +
      "<div class='vmh-rep__head'>" +
        "<div class='vmh-rep__score'><span class='vmh-rep__n vm-num'>" + vmEsc(num(s.score)) + "</span><span class='vmh-rep__of'>/ " + vmEsc(num(max)) + "</span></div>" +
        "<div class='vmh-rep__sum'><p class='vmh-rep__title'>AI Visibility Score</p>" +
          "<p class='vmh-rep__band'><span class='vm-state vm-state--" + stateOf(pct) + "'>" + vmEsc(band) + "</span></p>" +
          (bench ? "<p class='vmh-rep__bench'>" + vmEsc(bench) + "</p>" : "") +
        "</div></div>";
    if (cats.length) {
      h += "<p class='vmh-rep__k'>Category scores</p><ol class='vmh-rep__gates vmh-rep__cats'>" + cats.map(function (c) {
        var p = Math.max(0, Math.min(100, (c.score / c.max) * 100));
        return "<li><span class='g'>" + vmEsc(c.name) + "</span><span class='vm-meter vm-meter--" + stateOf(p) + "' aria-hidden='true'><i style='width:" + p.toFixed(1) + "%'></i></span>" +
          "<span class='v vm-num'>" + vmEsc(num(c.score)) + "<small> / " + vmEsc(num(c.max)) + "</small></span></li>";
      }).join("") + "</ol>";
    }
    if (issues.length) {
      h += "<div class='vmh-rep__pri'><p class='vmh-rep__k'>Top priorities from this sample</p><ul class='vmh-rep__issues'>" + issues.map(function (x, i) {
        var inst = Array.isArray(x.instances) && x.instances[0] ? x.instances[0] : null;
        var where = inst && inst.page_url ? pathOf(inst.page_url) : (Array.isArray(x.pages) && x.pages[0] ? pathOf(x.pages[0]) : "");
        var seenOn = typeof x.page_count === "number" && x.page_count > 1 ? " and " + (x.page_count - 1) + (x.page_count === 2 ? " other page" : " other pages") : "";
        var snip = inst && inst.snippet ? tidy(String(inst.snippet)) : "";
        if (snip.length > 170) snip = snip.slice(0, 167).replace(/\s+\S*$/, "") + "...";
        var hint = x.hint && !GENERIC_HINT.test(String(x.hint)) ? tidy(x.hint) : "";
        return "<li><span class='vmh-rep__ir'>0" + (i + 1) + "</span><div>" +
          (x.category_name ? "<p class='vmh-rep__ic'>" + vmEsc(x.category_name) + "</p>" : "") +
          "<p class='vmh-rep__ip'>" + vmEsc(tidy(x.problem)) + "</p>" +
          (where ? "<p class='vmh-rep__ie'><b>Evidence:</b> found on " + vmEsc(where) + vmEsc(seenOn) + "." + (snip ? " We read: " + vmEsc(snip) : "") + "</p>" : "") +
          (hint ? "<p class='vmh-rep__ih'><b>Fix:</b> " + vmEsc(hint) + "</p>" : "<p class='vmh-rep__ih'>Your full results explain the fix for this page.</p>") +
          "</div></li>";
      }).join("") + "</ul></div>";
    }
    if (pages.length) {
      h += "<div class='vmh-rep__pages'><p class='vmh-rep__k'>Pages we read</p><ul>" + pages.map(function (u) {
        return "<li>" + vmEsc(pathOf(u)) + "</li>";
      }).join("") + "</ul><p class='vmh-rep__sample'>This was a free sample of " + pages.length + (pages.length === 1 ? " page" : " pages") +
        ". A full scan reads more of your site and ranks every issue.</p></div>";
    }
    h += "</div>";
    return h;
  };
  var runSequence = function (form, id, host, url, email) {
    var box = form.querySelector("[data-vm-seq]");
    if (!box || !id || !window.fetch) return;
    var rows = GATES.map(function (g, i) {
      return "<li data-gate='" + g.key + "' data-state='wait'><span class='n'>0" + (i + 1) + "</span><span class='g'>" + g.name + "</span><span class='s'>Waiting</span></li>";
    }).join("");
    box.innerHTML = "<p class='vmh-seq__t'>Analyzing " + vmEsc(host) + "</p>" +
      "<p class='vmh-seq__sub'>Each line updates when the engine finishes that part of the scan. Gates 02 to 05 are checked together, so they finish at the same moment.</p>" +
      "<ol role='status' aria-live='polite'>" + rows + "</ol><div class='vmh-seq__base' hidden></div>";
    box.hidden = false;
    var li = function (k) { return box.querySelector("li[data-gate='" + k + "']"); };
    var set = function (keys, state, text) {
      keys.forEach(function (k) { var r = li(k); if (!r) return; r.setAttribute("data-state", state); r.querySelector(".s").textContent = text; });
    };
    var all = GATES.map(function (g) { return g.key; });
    var later = ["read", "follow", "quote", "trust"];
    var started = Date.now(), tries = 0, finished = false;
    var resultsLink = "/pages/results?id=" + encodeURIComponent(id);
    var base = box.querySelector(".vmh-seq__base");
    var ctx = { id: id, url: url || "", email: email || "", score: "", resultsLink: resultsLink };
    var fallback = function (text, withActions) {
      if (finished) return; finished = true;
      base.hidden = false;
      base.innerHTML = "<p class='vmh-seq__sub'>" + vmEsc(text) + "</p>" +
        (withActions ? actionsHtml(ctx) : "<a class='vm-link' href='" + resultsLink + "' data-vm-event='scan_results_click' data-vm-location='scan_sequence'>Open your results page</a>");
    };
    var complete = function (s) {
      if (finished) return; finished = true;
      set(all, "done", "Done");
      if (s.target_url && /^https?:\/\//i.test(s.target_url)) ctx.url = s.target_url;
      ctx.score = num(s.score);
      var max = Number(s.max_score) || 100;
      var band = s.band || bandOf((Number(s.score) / max) * 100);
      var issueCount = typeof s.issue_count === "number" ? s.issue_count : (Array.isArray(s.issues) ? s.issues.length : 0);
      // our sample report on the right becomes the visitor's report; the final call to action has no report panel, so it shows the report under its summary
      var section = form.closest ? form.closest("section") : null;
      var fig = section ? section.querySelector(".vmh-hero__report") : null;
      var reportHtml = renderReport(s, host);
      var html = "<h3>Your free sample scan of " + vmEsc(host) + " is ready.</h3>" +
        "<p class='vmh-seq__score'>Your AI Visibility Score is <b>" + vmEsc(ctx.score) + " of " + vmEsc(num(max)) + "</b>" + (band ? ", in the " + vmEsc(band) + " band" : "") + ".</p>" +
        "<p class='vmh-seq__sub'>" + (issueCount > 0
          ? "This sample found " + issueCount + (issueCount === 1 ? " issue" : " issues") + ", and your report shows " + (issueCount >= 3 ? "the first 3 priorities" : "the top priorities") + " with a fix for each."
          : "Your report shows your score and your category scores.") + "</p>" +
        actionsHtml(ctx);
      base.innerHTML = html;
      base.hidden = false;
      if (fig) {
        fig.innerHTML = reportHtml + "<figcaption class='vmh-hero__cap'>These are the results of your free sample scan" + (todayText() ? " on " + vmEsc(todayText()) : "") + ". They come from a small set of pages, so a full scan can find more.</figcaption>";
        fig.removeAttribute("aria-labelledby");
        fig.setAttribute("aria-label", "Your report for " + host);
        fig.classList.add("is-yours");
      } else {
        base.insertAdjacentHTML("beforeend", "<div class='vmh-seq__report'>" + reportHtml + "</div>");
      }
      // one scan_complete per scan per browser (the marker stays in this browser only)
      var once = "vm_sc_" + id, seen = false;
      try { seen = !!window.localStorage.getItem(once); window.localStorage.setItem(once, "1"); } catch (e) { /* storage blocked */ }
      if (!seen) {
        var secs = (Date.now() - started) / 1000;
        var bucket = secs < 60 ? "under_1m" : secs < 300 ? "1_to_5m" : secs < 900 ? "5_to_15m" : "over_15m";
        var data = { outcome: "scored", band: s.band || "", score_bucket: Math.floor((Number(s.score) || 0) / 10) * 10, tier: "T1", duration_bucket: bucket };
        vmPublish("vm_scan_complete", data);
        vmPush({ event: "scan_complete", vm_location: form.id === "vm-final-scan" ? "final" : "hero", vm_band: data.band, vm_score_bucket: data.score_bucket });
      }
    };
    var poll = function () {
      if (finished) return;
      tries++;
      fetch(ENGINE + "/scans/" + encodeURIComponent(id), { headers: { "Accept": "application/json" } })
        .then(function (r) { return r.status === 404 ? { _gone: true } : (r.ok ? r.json() : { _retry: true }); })
        .then(function (d) {
          if (finished) return;
          if (d._gone) { fallback("We could not find that scan yet. Your results will still arrive by email."); return; }
          var st = String((d && d.status) || "").toLowerCase();
          if (st === "crawling" || st === "queued" || st === "") {
            set(["find"], st === "crawling" ? "active" : "wait", st === "crawling" ? "Checking" : "Waiting");
          } else if (st === "crawled" || st === "scoring" || st === "reporting") {
            set(["find"], "done", "Done");
            set(later, "active", "Checking");
          } else if (st === "done" || st === "complete" || st === "completed") {
            set(["find"], "done", "Done");
            fetch(ENGINE + "/scans/" + encodeURIComponent(id) + "/summary", { headers: { "Accept": "application/json" } })
              .then(function (r) { return r.ok ? r.json() : null; })
              .then(function (s) {
                if (s && s.ready && s.score != null) { complete(s); return; }
                set(all, "done", "Done");
                fallback("This site could not be scored, often because it blocks AI crawlers. Your results page explains what we found, and our consultants can open the site up for you.", true);
              })
              .catch(function () { set(all, "done", "Done"); fallback("Your scan finished. Open your results page to see the score.", true); });
            return;
          } else if (st === "needs_review") {
            fallback("This scan needs a manual look from our team. Your results will follow by email.");
            return;
          } else if (st === "error" || st === "failed") {
            fallback("This scan did not finish. Please run it again, or reply to your scan email and we will look into it.");
            return;
          }
          if (tries >= 120) { fallback("Your scan is taking longer than usual. The results will arrive by email as soon as they are ready."); return; }
          setTimeout(poll, 3000);
        })
        .catch(function () {
          if (tries >= 120) { fallback("We lost the connection, but your results will still arrive by email."); return; }
          setTimeout(poll, 3000);
        });
    };
    poll();
  };

  // 3. scan bars (every form with data-vm-scan: the hero and the final call to action)
  var scanForms = document.querySelectorAll("form[data-vm-scan]");
  Array.prototype.forEach.call(scanForms, function (form) {
    if (form.dataset.vmBound) return;
    form.dataset.vmBound = "1";
    var urlEl = form.querySelector("[name='target_url']");
    var emailEl = form.querySelector("[name='email']");
    var btn = form.querySelector("button[type='submit']");
    var msg = form.querySelector(".msg");
    var say = function (html, kind) { msg.innerHTML = html; msg.className = "msg is-" + kind; };
    var normUrl = function (v) { v = (v || "").trim(); if (v && !/^https?:\/\//i.test(v)) v = "https://" + v; return v; };
    var validEmail = function (v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v); };
    var mark = function (el, bad) { if (bad) { el.setAttribute("aria-invalid", "true"); } else { el.removeAttribute("aria-invalid"); } };

    form.addEventListener("submit", function (ev) {
      ev.preventDefault();
      var url = normUrl(urlEl.value), email = (emailEl.value || "").trim();
      mark(urlEl, false); mark(emailEl, false);
      if (!url) { mark(urlEl, true); say("Enter your website address.", "error"); urlEl.focus(); return; }
      try { new URL(url); } catch (e) { mark(urlEl, true); say("That website address does not look right. Try a form like example.com.", "error"); urlEl.focus(); return; }
      if (!validEmail(email)) { mark(emailEl, true); say("Enter a valid email address, so we can send your results.", "error"); emailEl.focus(); return; }
      btn.disabled = true;
      var label = btn.textContent;
      btn.textContent = "Starting your scan";
      say("Submitting your scan.", "info");
      fetch(ENGINE + "/scans", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ target_url: url, email: email, tier: "T1" })
      }).then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (d) { return { status: res.status, data: d }; });
      }).then(function (r) {
        var err = r.data && r.data.error, id = r.data && r.data.id;
        if (r.status === 202) {
          btn.textContent = "Scan started";
          var host = ""; try { host = new URL(url).hostname.replace(/^www\./, ""); } catch (e2) { host = url; }
          // the lead moment, with the same name and fields as snippets/vm-free-scan.liquid (host only, never the email)
          vmPublish("vm_free_scan_submitted", { product_handle: host.replace(/[^a-z0-9-]/g, "-"), product_title: host, product_type: "free-scan", available: true });
          vmPush({ event: "scan_start", vm_location: form.id === "vm-final-scan" ? "final" : "hero" });
          say("Scan started. We will also email your scorecard to <strong>" + vmEsc(email) + "</strong>.", "ok");
          runSequence(form, id, host, url, email);
        } else if (r.status === 409 && err === "free_scan_used") {
          say("You already ran a free scan on this email in the last 30 days. " +
            (id ? "<a href='/pages/results?id=" + encodeURIComponent(id) + "'>View your results.</a> " : "") +
            "When you want the deeper scan, <a href='/products/advanced-plan'>see scan and fixes</a>.", "ok");
          btn.disabled = false; btn.textContent = label;
        } else if (r.status === 429) {
          say("Too many requests right now. Please try again a little later.", "error");
          btn.disabled = false; btn.textContent = label;
        } else if (r.status === 400) {
          mark(urlEl, true);
          say("Check your website address and email, then try again.", "error");
          btn.disabled = false; btn.textContent = label;
        } else {
          say("Something went wrong starting your scan. Please try again.", "error");
          btn.disabled = false; btn.textContent = label;
        }
      }).catch(function () {
        say("We could not reach the scan engine. Please try again in a moment.", "error");
        btn.disabled = false; btn.textContent = label;
      });
    });
  });

  // links to #scan: move the visitor into the first scan bar
  document.addEventListener("click", function (e) {
    var a = e.target.closest ? e.target.closest("a[href='#scan']") : null;
    var form = document.querySelector("form[data-vm-scan]");
    var urlEl = form ? form.querySelector("[name='target_url']") : null;
    if (!a || !urlEl) return;
    e.preventDefault();
    var calm = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    form.scrollIntoView({ behavior: calm ? "auto" : "smooth", block: "center" });
    try { urlEl.focus({ preventScroll: true }); } catch (x) { urlEl.focus(); }
  });

  // 4. proposal modal
  var dlg = document.getElementById("vm-proposal-modal");
  if (dlg && typeof dlg.showModal === "function") {
    var pform = document.getElementById("vm-pm-form");
    var pmsg = document.getElementById("vm-pm-msg");
    var psite = document.getElementById("vm-pm-site");
    var pemail = document.getElementById("vm-pm-email");
    var ptext = document.getElementById("vm-pm-text");
    var psubmit = pform.querySelector("button[type='submit']");
    var opener = null, openLocation = "", started = false, scanNote = "";
    var html = document.documentElement;
    var pmsay = function (t, kind) { pmsg.textContent = t; pmsg.className = "vm-pm__msg is-" + kind; };

    document.addEventListener("click", function (e) {
      var el = e.target.closest ? e.target.closest("[data-vm-proposal]") : null;
      if (!el) return;
      e.preventDefault();
      opener = el;
      openLocation = el.getAttribute("data-vm-location") || "";
      // a button from a finished scan carries the visitor's website, email, scan id and score; they prefill the form,
      // and the scan id and score also travel in the message, since the modal has no field of its own for them
      var pre = { site: el.getAttribute("data-vm-pm-site") || "", email: el.getAttribute("data-vm-pm-email") || "", scan: el.getAttribute("data-vm-pm-scan") || "", score: el.getAttribute("data-vm-pm-score") || "" };
      scanNote = "";
      if (pre.scan) {
        scanNote = "Free scan " + pre.scan + (pre.score ? ", score " + pre.score + " of 100" : "") + ".";
        if (pre.site && !psite.value) psite.value = pre.site;
        if (pre.email && !pemail.value) pemail.value = pre.email;
        if (!ptext.value) ptext.value = "Please send me a proposal with a roadmap and a quoted price. " + scanNote;
      }
      if (!dlg.open) { dlg.showModal(); html.classList.add("vm-pm-open"); }
      try { (pre.scan && psite.value && pemail.value ? ptext : psite).focus(); } catch (x) { /* ignore */ }
    });
    dlg.addEventListener("click", function (e) {
      if (e.target === dlg || (e.target.closest && e.target.closest("[data-vm-pm-close]"))) dlg.close();
    });
    dlg.addEventListener("close", function () {
      html.classList.remove("vm-pm-open");
      if (opener && opener.focus) { try { opener.focus(); } catch (x) { /* ignore */ } }
    });

    pform.addEventListener("submit", function (e) {
      e.preventDefault();
      if (started) return;
      var hp = pform.querySelector("input[name='hp']");
      if (hp && hp.value) return;
      if (!psite.value || !psite.checkValidity() || !pemail.value || !pemail.checkValidity()) {
        pmsay("Please enter a full website address that starts with https:// and a valid email address.", "error");
        return;
      }
      started = true;
      psubmit.disabled = true;
      var typed = (ptext.value || "").trim();
      if (scanNote && typed.indexOf(scanNote) < 0) typed = (typed + " " + scanNote).trim();
      var message = "Proposal request. " + typed;
      var done = false;
      var nativeSubmit = function () {
        if (done) return; done = true;
        ptext.value = message;
        HTMLFormElement.prototype.submit.call(pform);
      };
      var thanks = function () {
        if (done) return; done = true;
        pform.reset();
        pmsay("Thank you. We received your request and a person will reply within 1 business day.", "ok");
        psubmit.textContent = "Request sent";
        try {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ event: "proposal_request_submitted", vm_location: openLocation });
          if (typeof window.gtag === "function") window.gtag("event", "proposal_request_submitted", { vm_location: openLocation });
        } catch (x) { /* never block */ }
        // Managed service request (directive section 54) for the first party collector. The quote form on Managed is gone, so the modal
        // is where this request now happens. The GA4 lead pixel does not subscribe to this name, so GA4 keeps its single count above.
        vmPublish("vm_proposal_request_submitted", { form_name: "proposal_modal", location: openLocation, page: window.location.pathname });
      };
      var payload = { email: pemail.value, website: psite.value, service: "managed", message: message, page: window.location.pathname, hp: "" };
      setTimeout(nativeSubmit, 8000);
      if (!window.fetch) { nativeSubmit(); return; }
      fetch("/apps/visibility-mesh/quote", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify(payload),
        credentials: "same-origin",
        keepalive: true
      }).then(function (r) { if (r && r.ok) { thanks(); } else { nativeSubmit(); } }, nativeSubmit);
    });
  }

  // 5. reveal on scroll: content is visible by default. Only elements below the fold are hidden and animated,
  //    and a 1.5 second fallback forces everything visible, so fast or programmatic scrolling never leaves a blank band.
  (function () {
    var els = document.querySelectorAll(".vm-home .rv");
    if (!els.length || !("IntersectionObserver" in window)) return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    var vh = window.innerHeight || 800;
    var root = document.documentElement;
    var below = [];
    Array.prototype.forEach.call(els, function (el) {
      if (el.getBoundingClientRect().top > vh) { el.classList.add("rv-arm"); below.push(el); }
    });
    if (!below.length) return;
    root.classList.add("vm-rv");
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { en.target.classList.add("is-in"); io.unobserve(en.target); }
      });
    }, { rootMargin: "0px 0px 15% 0px", threshold: 0 });
    below.forEach(function (el) { io.observe(el); });
    setTimeout(function () {
      below.forEach(function (el) { el.classList.add("is-in"); });
      io.disconnect();
    }, 1500);
  })();
})();
